import React, { useEffect, useState } from 'react';
import { useNotice } from '../shared/NoticeProvider';
import { fetchWithAuth } from '../../../utils/api';
import { PencilIcon, UserGroupIcon, PlusIcon, TrashIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import V3OrganizationForm from './V3OrganizationForm';
import { Heading } from '../../ui/heading';
import { Timestamp } from '../../ui/timestamp';

// Organization interface from the API
interface Organization {
  organization_id: string;
  organization_code: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

// Form data interface for creating/updating organizations
interface OrganizationFormData {
  organization_id?: string;
  organization_code: string;
  name: string;
  description?: string;
}

const V3OrganizationsManagement: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { show: showNotice } = useNotice();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | undefined>(undefined);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchOrganizations();
  }, []);
  
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth('/v1/auth/admin/organizations');
      setOrganizations(data);
      setError(null);
    } catch (err) {
      setError('Failed to load organizations. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateOrganization = () => {
    setSelectedOrganization(undefined);
    setIsModalOpen(true);
  };
  
  const handleEditOrganization = (organization: Organization) => {
    setSelectedOrganization(organization);
    setIsModalOpen(true);
  };
  
  const handleSubmitOrganization = async (organizationData: OrganizationFormData) => {
    try {
      if (organizationData.organization_id) {
        // Update existing organization
        await fetchWithAuth(`/v1/auth/admin/organizations/${organizationData.organization_id}`, {
          method: 'PUT',
          body: JSON.stringify(organizationData)
        });
      } else {
        // Create new organization
        await fetchWithAuth('/v1/auth/admin/organizations', {
          method: 'POST',
          body: JSON.stringify(organizationData)
        });
      }
      
      // Close modal and refresh organizations
      setIsModalOpen(false);
      fetchOrganizations();
      showSuccessMessage(organizationData.organization_id ? 'Organization updated successfully' : 'Organization created successfully');
    } catch (err) {
      throw err;
    }
  };
  
  const handleDeleteOrganization = async (organizationId: string) => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      return;
    }
    
    try {
      await fetchWithAuth(`/v1/auth/admin/organizations/${organizationId}`, {
        method: 'DELETE'
      });
      
      // Remove the organization from the state
      setOrganizations(currentOrgs => 
        currentOrgs.filter(org => org.organization_id !== organizationId)
      );
      
      showSuccessMessage('Organization deleted successfully');
    } catch (err: any) {
      // Check if there are users in the organization
      if (err.message && err.message.includes('Cannot delete organization with')) {
        showNotice({ type: 'warning', message: 'This organization has users assigned. Please delete or reassign these users first.' });
      } else {
        showNotice({ type: 'error', message: 'Failed to delete organization. Please try again.' });
        console.error(err);
      }
    }
  };
  
  const showSuccessMessage = (message: string) => {
    const tempMessage = document.createElement('div');
    tempMessage.className = 'fixed top-4 right-4 bg-green-50 text-green-800 px-4 py-3 rounded-lg shadow-lg border border-green-200 flex items-center space-x-2';
    tempMessage.innerHTML = `
      <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      <span>${message}</span>
    `;
    document.body.appendChild(tempMessage);
    
    // Remove message after 3 seconds
    setTimeout(() => {
      tempMessage.remove();
    }, 3000);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
          <p className="text-red-800 mb-4">{error}</p>
          <button 
            onClick={fetchOrganizations}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <Heading level={2} className="text-gray-900 flex items-center">
              <BuildingOfficeIcon className="w-7 h-7 mr-3 text-gray-700" />
              Organization Management
            </Heading>
            <p className="text-gray-600 mt-1">
              Manage organizations and their settings
            </p>
          </div>
          <button 
            onClick={handleCreateOrganization}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center hover:bg-blue-700 transition-colors shadow-sm"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add New Organization
          </button>
        </div>
        
        {/* Organizations Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {organizations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <BuildingOfficeIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No organizations found</p>
                    </td>
                  </tr>
                ) : (
                  organizations.map(org => (
                    <tr key={org.organization_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{org.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {org.organization_code}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {org.description ? org.description : <em className="text-gray-400">No description</em>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <Timestamp value={org.created_at} format="absolute" />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button 
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
                            onClick={() => handleEditOrganization(org)}
                            title="Edit Organization"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button 
                            className="p-2 rounded-lg hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                            onClick={() => navigate('/v3/settings/users', { state: { orgId: org.organization_id }})}
                            title="View Users"
                          >
                            <UserGroupIcon className="w-4 h-4" />
                          </button>
                          <button 
                            className="p-2 rounded-lg hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors"
                            onClick={() => handleDeleteOrganization(org.organization_id)}
                            title="Delete Organization"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Organization Form Modal */}
      {isModalOpen && (
        <V3OrganizationForm
          organization={selectedOrganization}
          onSubmit={handleSubmitOrganization}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default V3OrganizationsManagement;
